import { computed, ref } from 'vue';
import axios from 'axios';
import { save as saveCookieConsent } from '@/routes/cookie-consent';
import { useCookiePreferencesBus } from '@/composables/buses';
import type { ConsentSharedProp } from '@/types/models';

export const COOKIE_CONSENT_VERSION = 1;

export interface CookieConsent {
    version: number;
    necessary: true;
    analytics: boolean;
    updated_at: string | null;
}

interface CookieConsentInput {
    analytics: boolean;
}

// Module-level singleton shared by all callers. Hydrated once in app.ts from
// the Inertia shared prop (server-side cookie / DB), so the initial value is
// available on first paint with zero flash and works across subdomains.
const consent = ref<CookieConsent | null>(null);

export function hydrateConsent(props: ConsentSharedProp | null | undefined): void {
    if (!props?.has_choice) {
        consent.value = null;

        return;
    }
    consent.value = {
        version: COOKIE_CONSENT_VERSION,
        necessary: true,
        analytics: props.analytics === true,
        updated_at: null,
    };
}

export function readCookieConsent(): CookieConsent | null {
    return consent.value;
}

export function hasAnalyticsConsent(): boolean {
    return consent.value?.analytics === true;
}

function persist(preferences: CookieConsentInput): void {
    // Update locally first so reactive consumers (banner, PostHog watcher) react
    // immediately — no round-trip wait.
    consent.value = {
        version: COOKIE_CONSENT_VERSION,
        necessary: true,
        analytics: preferences.analytics,
        updated_at: new Date().toISOString(),
    };

    // Domain-agnostic endpoint: sets parent-domain cookie for guests, also saves
    // to user.preferences for authed users. The Wayfinder route emits a relative
    // /cookie-consent so it works from both the landing and app subdomains.
    void axios.post(saveCookieConsent.url(), preferences);
}

export function useCookieConsent() {
    const hasChoice = computed<boolean>(() => Boolean(consent.value));
    const analytics = computed<boolean>(() => consent.value?.analytics === true);

    function acceptAll(): void {
        persist({ analytics: true });
    }

    function rejectOptional(): void {
        persist({ analytics: false });
    }

    function savePreferences(preferences: CookieConsentInput): void {
        persist(preferences);
    }

    function openPreferences(): void {
        useCookiePreferencesBus().emit();
    }

    return {
        consent: computed(() => consent.value),
        hasChoice,
        analytics,
        acceptAll,
        rejectOptional,
        savePreferences,
        openPreferences,
    };
}
